// Single questionnaire question for the calendar sidebar "phone" preview.
// Copy of the workouts module's Question rendering the full UI per inputType
// (multiple-choice, yes/no, range, input), populated from the question's response.
// Interaction is blocked by the overlay in CalendarExerciseDetail.
import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import TBRangeSlider from '../../../../shared/components/TBRangeSlider/TBRangeSlider';
import { BoxShadow } from '../../../../shared/GlobalStyles';

const Container = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: ${BoxShadow};
  border-radius: 12px;
  flex-direction: column;
  padding: 15px;
  background: white;
  min-height: 100px;
  margin-top: ${(props) => (props.isFirst ? '0px' : '15px')};
  white-space: normal;
`;

const FieldAndSlider = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  flex: 1;
`;

const StyledInput = styled('input')`
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: 11px;
  border: 1px solid #ccc;
  text-align: center;
  width: 30%;
  border-radius: 4px;
  height: 30px;
  background: white;
`;

const TextArea = styled('textarea')`
  width: 100%;
  padding: 10px;
  line-height: 1.5;
  border-radius: 5px;
  border: 1px solid #ccc;
  margin-top: 5px;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  resize: none;
`;

const SliderContainer = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  margin-left: 20px;
`;

const RangeBox = styled('div')`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  font-size: 10px;
  font-family: 'Nunito Sans';
  flex: 1;
`;

const TitleAndDescription = styled('div')`
  width: 100%;
  display: flex;
  font-size: 10px;
  font-family: 'Nunito Sans';
  flex-direction: column;
  margin-bottom: 10px;
`;

const Title = styled('div')`
  width: 100%;
  display: flex;
  font-size: 12px;
  font-family: 'Nunito Sans';
  white-space: normal;
`;

const Description = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  flex: 1;
`;

const YesOption = styled('div')`
  display: flex;
  justify-content: space-between;
  height: 40px;
  width: 100%;
  align-items: center;
`;

const YesNoCircle = styled('div')`
  height: 30px;
  width: 30px;
  border: ${(props) => (props.isActive ? '1px solid #ff9a44' : '1px solid lightgrey')};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const YesNoText = styled('div')`
  display: flex;
  font-size: 14px;
  font-family: 'Nunito Sans';
  white-space: normal;
`;

const QuestionSeparator = styled('div')`
  width: 100%;
  height: 1px;
  background: lightgrey;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const TagSelectTrigger = styled('div')`
  font-weight: 500;
  color: grey;
  background: #ffffff;
  user-select: none;
  border-radius: 4px;
  min-height: 38px;
  padding: 8px;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  position: relative;
  border: none;
  flex: 1;
`;

const TriggerAndIcon = styled('div')`
  width: 100%;
  display: flex;
  border-radius: 4px;
  border: 1px solid #d8d8d8;
  align-items: center;
  white-space: nowrap;
`;

const HeaderDivider = styled('div')`
  border: 0.5px solid lightgrey;
  height: 60%;
`;

const IconContainer = styled('div')`
  width: 30px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const noop = () => {};

const CalendarQuestion = ({ question, first }) => {
  const response = question.response || {};

  const renderInput = () => {
    if (question.inputType === 'MULTIPLE-CHOICE') {
      return (
        <TriggerAndIcon>
          <TagSelectTrigger>{response.displayValue || 'Select an answer'}</TagSelectTrigger>
          <HeaderDivider />
          <IconContainer>
            <IcomoonReact iconSet={iconSet} size={15} icon='down-arrow' color='lightgrey' />
          </IconContainer>
        </TriggerAndIcon>
      );
    }
    if (question.inputType === 'YES-NO') {
      return (question.choices || []).map((choice, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${choice.value}-${idx}`}>
          <YesOption>
            <YesNoText>{choice.displayValue}</YesNoText>
            <YesNoCircle isActive={choice.displayValue === response.displayValue}>
              {choice.displayValue === response.displayValue ? (
                <IcomoonReact iconSet={iconSet} size={15} icon='checkmark' color='#ff9a44' />
              ) : null}
            </YesNoCircle>
          </YesOption>
          {idx === 0 ? <QuestionSeparator /> : null}
        </React.Fragment>
      ));
    }
    if (question.inputType === 'RANGE') {
      const sliderValue = parseInt(response.value, 10) || 0;
      return (
        <>
          <FieldAndSlider>
            <StyledInput readOnly value={sliderValue} type='number' />
            <SliderContainer>
              <TBRangeSlider
                minValue={question.minRangeValue}
                maxValue={question.maxRangeValue}
                value={sliderValue}
                onChange={noop}
              />
            </SliderContainer>
          </FieldAndSlider>
          <RangeBox>
            {(question.minRangeValue || question.minRangeValue === 0) && question.maxRangeValue
              ? `Range: ${question.minRangeValue} - ${question.maxRangeValue}`
              : null}
          </RangeBox>
        </>
      );
    }
    if (question.inputType === 'INPUT') {
      return <TextArea readOnly value={response.value || ''} />;
    }
    return null;
  };

  return (
    <Container isFirst={first}>
      <TitleAndDescription>
        <Title>{question.title}</Title>
        <Description>{question.description}</Description>
      </TitleAndDescription>
      {renderInput()}
    </Container>
  );
};

CalendarQuestion.defaultProps = {
  first: false,
};

CalendarQuestion.propTypes = {
  question: PropTypes.instanceOf(Object).isRequired,
  first: PropTypes.bool,
};

export default CalendarQuestion;
